import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Stack } from "@mui/material";
import {
  Create,
  useGetIdentity,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import Swal from "sweetalert2";
import { dataProvider } from "../../../provider";
import { omit } from "lodash";
import Mbaas from "../../../provider/Mbaas";
import RewardForm from "./components/RewardForm";
import { applyPickupLeadTime } from "./payloadUtils";

interface CreateServiceRewardsProps {
  onClose: () => void;
}

export const buildCreatePayload = (values: any, userId?: string | number) => {
  const {
    item_detail,
    category,
    item_key,
    item_group,
    availability_by_service,
    is_bonus,
    bonus_item_key,
    start,
    end,
    limit,
    amount,
    ...restValue
  } = values;

  const itemPayload: any = {
    category,
    item_key,
    item_group,
  };

  if (category === "RELOAD") {
    itemPayload.limit = limit;
    itemPayload.amount = amount;
  } else if (category === "BALANCE") {
    itemPayload.amount = amount;
  }

  let codes: any = {};

  if (item_detail?.codes) {
    codes = item_detail.codes.map((item: any) => ({
      code: item.code,
      redeemed: false,
      purchased: false,
      redeemed_at: "",
      purchased_at: "",
    }));
  }

  const itemDetail: any = applyPickupLeadTime(
    {
      codes,
      image: item_detail?.image,
      item: itemPayload,
    },
    item_detail,
    category,
  );

  const payloads: any = {
    ...omit(restValue, [
      "item_label",
      "redemption_amount",
      "redeem_limits",
      "bonus_item_group",
    ]),
    item_label: {},
    redemption_amount: {},
    redeem_limits: {},
    availability_by_service: [availability_by_service],
    author_id: userId,
    item_detail: itemDetail,
    update_history: [],
  };

  if (values.item_label) {
    values.item_label.forEach((m: any) => {
      payloads["item_label"][m.country] = m.label;
    });
  }

  if (values.redemption_amount) {
    values.redemption_amount.forEach((m: any) => {
      payloads["redemption_amount"][m.country] = {
        coin: Number(m.coin),
        point: Number(m.point),
      };
    });
  }

  if (values.redeem_limits) {
    payloads["redeem_limits"] = {
      global_daily_limit:
        Number(values.redeem_limits.global_daily_limit) || 0,
      global_monthly_limit:
        Number(values.redeem_limits.global_monthly_limit) || 0,
      global_total_limit:
        Number(values.redeem_limits.global_total_limit) || 0,
      individual_daily_limit:
        Number(values.redeem_limits.individual_daily_limit) || 0,
      individual_monthly_limit:
        Number(values.redeem_limits.individual_monthly_limit) || 0,
      individual_total_limit:
        Number(values.redeem_limits.individual_total_limit) || 0,
      minimum_balance: {
        coin: Number(values.redeem_limits.minimum_balance.coin) || 0,
        point: Number(values.redeem_limits.minimum_balance.point) || 0,
      },
    };
  }

  if (is_bonus) {
    payloads.item_detail.bonus_for = {
      item_keys: bonus_item_key,
      start,
      end,
    };
  }

  return payloads;
};

export const CreateServiceRewards: React.FC<CreateServiceRewardsProps> = (
  props,
) => {
  const { onClose, ...restProps } = props;
  const { isLoading: isIdLoading, identity: user } = useGetIdentity();

  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const [loading, setLoading] = useState<boolean>(false);

  const isLoading = loading || isIdLoading;

  const onSubmitHandler = async (values: any) => {
    setLoading(true);
    try {
      const item_detail = values.item_detail || {};
      const payloads = buildCreatePayload(values, user?.id);

      if (item_detail.image) {
        const uploadImage: any = await Mbaas.client.storage.upload({
          file: item_detail.image?.rawFile,
          params: {
            filename: `reward-${Date.now()}`,
            bucket: "banners",
          },
        });

        const download = await Mbaas.client.storage.downloadUrl({
          params: {
            bucket: "banners",
          },
          fileId: uploadImage?.data?.id,
        });

        const urlImg = download.data.url.split("?");
        payloads["item_detail"]["image"] = urlImg[0];
      }

      const resp = await dataProvider.create("rewardCatalogues", {
        data: payloads,
      });
      if (resp.data) {
        notify("ra.notification.created", {
          type: "info",
          messageArgs: { smart_count: 1 },
          undoable: true,
        });
        redirect(`/services/rewards`);
        refresh();
        Swal.fire("Success!", "", "success");
      }
    } catch (error) {
      Swal.fire(`Request failed: ${error}`, "", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Create resource="rewardCatalogues" title=" &rsaquo; Create" {...restProps}>
      <Box pt={5} width={{ xs: "100vW", sm: 600 }} mt={{ xs: 2, sm: 1 }}>
        <Stack
          direction="row"
          p={0}
          sx={{ position: "absolute", right: 10, zIndex: 100 }}
        >
          <IconButton color="error" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <RewardForm
          isLoading={isLoading}
          onSubmit={onSubmitHandler}
          type="add"
        />
      </Box>
    </Create>
  );
};
